import React, { useEffect, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useTodaysGuidance } from "./useTodaysGuidance";
import {
  CADENCE_BADGE_STYLES,
  CADENCE_GENERIC_LABEL,
  CADENCE_ICON,
  CADENCE_ORDER,
  CADENCE_SOLID_COLOR,
  CadenceKey,
} from "../../Utils/Guidance/guidanceCadence";
import { GuidanceRole } from "../../Utils/Guidance/guidanceRole";

interface FloatCardProps {
  userRole: string | { name?: string } | null | undefined;
  role: GuidanceRole | null;
  cadenceKey: CadenceKey;
  onClose: () => void;
  /** "Full View" — expands this cadence back into the Guide Drawer. */
  onFullView: (cadenceKey: CadenceKey) => void;
}

/**
 * Draggable, persistent mini-card — the "Float" detail mode confirmed in the
 * live prototype (reachable from the Guide Drawer's "Float ↗" button, and
 * directly from each Hub card). Stays mounted (in RoleBasedLayout, alongside
 * GuideDrawer) so dragging it and then navigating leaves it in place.
 */
const FloatCard: React.FC<FloatCardProps> = ({
  userRole,
  role,
  cadenceKey: initialCadenceKey,
  onClose,
  onFullView,
}) => {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const navigate = useNavigate();
  const [cadenceKey, setCadenceKey] = useState<CadenceKey>(initialCadenceKey);
  // Default top-right, matching the prototype's float-modal (top:80px, right:24px)
  const [position, setPosition] = useState<{ left: number; top: number }>(
    () => ({
      left: Math.max(16, window.innerWidth - 400 - 24),
      top: 80,
    })
  );

  useEffect(() => {
    setCadenceKey(initialCadenceKey);
  }, [initialCadenceKey]);

  const { playbook, personaContent, resolvedLinks, isToday } =
    useTodaysGuidance(userRole, {
      cadenceOverride: cadenceKey,
      initialPersona: role,
    });

  const cardRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{
    dragging: boolean;
    sx: number;
    sy: number;
    sl: number;
    st: number;
  } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragState.current;
      if (!d?.dragging || !cardRef.current) return;
      const w = cardRef.current.offsetWidth;
      const h = cardRef.current.offsetHeight;
      let nl = d.sl + (e.clientX - d.sx);
      let nt = d.st + (e.clientY - d.sy);
      nl = Math.max(0, Math.min(window.innerWidth - w, nl));
      nt = Math.max(0, Math.min(window.innerHeight - h, nt));
      setPosition({ left: nl, top: nt });
    };
    const onUp = () => {
      if (dragState.current) dragState.current.dragging = false;
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const onDragStart = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("button")) return;
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = {
      dragging: true,
      sx: e.clientX,
      sy: e.clientY,
      sl: rect.left,
      st: rect.top,
    };
    document.body.style.userSelect = "none";
  };

  const withOrg = (path: string) => (orgSlug ? `/${orgSlug}${path}` : path);
  const solidColor = CADENCE_SOLID_COLOR[cadenceKey];
  const personaLabel = personaContent.personaLabel;

  return (
    <div
      ref={cardRef}
      className="fixed w-[400px] bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden guidance-float-card"
      style={{ left: position.left, top: position.top }}
    >
      <div
        onMouseDown={onDragStart}
        className="flex items-center gap-1.5 px-3.5 pt-3 pb-3 border-b border-gray-200 cursor-move select-none"
      >
        <span className="text-[#9aa6b6] text-[13px] shrink-0">⠿</span>
        <span className="flex-1 text-[10px] font-bold uppercase tracking-wide text-[#2563eb] truncate">
          {personaLabel} Playbook
        </span>
        <button
          type="button"
          onClick={() => onFullView(cadenceKey)}
          className="text-[11px] font-semibold text-[#2563eb] bg-white border border-gray-200 rounded-[5px] px-[9px] py-[3px] hover:bg-gray-100"
        >
          Full View
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-[22px] h-[22px] shrink-0 flex items-center justify-center text-[12px] text-gray-500 bg-white border border-gray-200 rounded-[5px] hover:bg-gray-100"
        >
          ✕
        </button>
      </div>

      <div className="px-4 pt-5 pb-5 max-h-[75vh] overflow-y-auto">
        <div
          className="text-[11px] font-bold uppercase tracking-wide mb-2.5 flex items-center gap-1"
          style={{ color: solidColor }}
        >
          <span>{CADENCE_ICON[cadenceKey]}</span>
          <span>Cadence: {CADENCE_GENERIC_LABEL[cadenceKey]}</span>
        </div>
        <div className="text-sm font-bold text-gray-900 mb-2">
          {playbook.heading}
          {isToday && (
            <span className="ml-2 align-middle text-[10px] font-semibold text-white bg-purple-600 rounded-full px-2 py-0.5">
              Today
            </span>
          )}
        </div>
        <div className="text-xs text-gray-400 mb-4">{playbook.time}</div>

        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          What to do
        </div>
        <ul className="space-y-2.5 mb-4">
          {playbook.what.map((line, idx) => (
            <li key={idx} className="text-xs text-gray-700 flex gap-1.5">
              <span className="text-[#2563eb] font-bold shrink-0">→</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>

        {playbook.lookfor?.length ? (
          <>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              What to look for
            </div>
            <ul className="space-y-2.5 mb-5">
              {playbook.lookfor.map((line, idx) => (
                <li key={idx} className="text-xs text-gray-700 flex gap-1.5">
                  <span className="text-[#2563eb] font-bold shrink-0">→</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {resolvedLinks.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {resolvedLinks.map(link => (
              <button
                key={link.key}
                type="button"
                onClick={() => navigate(withOrg(link.path))}
                className="text-[11px] font-semibold px-2 py-1 rounded-[7px] border border-[#bfcefe] bg-[#eff4ff] text-[#2563eb] hover:bg-[#dbeafe]"
              >
                ↗ {link.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-nowrap gap-2 overflow-x-auto">
          {CADENCE_ORDER.filter(k => k !== cadenceKey).map(k => {
            const pillStyle = CADENCE_BADGE_STYLES[k];
            return (
              <button
                key={k}
                type="button"
                onClick={() => setCadenceKey(k)}
                className="shrink-0 text-[10px] font-medium px-2 py-[3px] rounded-full border hover:brightness-95 whitespace-nowrap"
                style={{
                  backgroundColor: pillStyle.bg,
                  color: pillStyle.text,
                  borderColor: pillStyle.border,
                }}
              >
                {CADENCE_ICON[k]} {CADENCE_GENERIC_LABEL[k]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() =>
            navigate(
              withOrg(
                `/guidance/playbooks?role=${role || ""}&cadence=${cadenceKey}`
              )
            )
          }
          className="text-[11px] font-semibold text-[#2563eb] hover:underline"
        >
          View in Playbooks Hub →
        </button>
      </div>
    </div>
  );
};

export default FloatCard;
